/* ──────────────────────────────────────────────────────────────────────────
 * Tile / base-map provider configuration.
 *
 * Deliberately isolated from every piece of business logic: nothing in the
 * geospatial layer, the intelligence engines or the UI knows where tiles come
 * from. Swapping a provider — or moving to self-hosted infrastructure on a
 * government network — is a change to this file and an env var, nothing else.
 *
 * WHY THIS MATTERS (brief §12): the public OpenStreetMap tile servers are run
 * by a volunteer foundation on donated capacity. Their tile usage policy does
 * not permit them to be used as production infrastructure for an operational
 * system, and heavy use gets blocked. The demo provider below is fine for a
 * POC on a laptop; it is NOT fine for Simhastha-scale deployment. See
 * PRODUCTION_NOTE at the bottom.
 * ────────────────────────────────────────────────────────────────────────── */

/* OpenStreetMap's attribution is a licence condition (ODbL), not a courtesy.
 * It must remain visible on the map wherever tiles or OSM-derived data are
 * shown. MapLibre renders this via the AttributionControl; do not remove it. */
export const OSM_ATTRIBUTION =
  '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors';

/* A provider is either raster (image tiles, keyless, works today) or vector
 * (needs a style URL and usually a key, unlocks MapLibre's real strengths). */
export const PROVIDER_KIND = { RASTER: "raster", VECTOR: "vector" };

export const TILE_PROVIDERS = {
  /* Default. Keyless and works offline-of-nothing, which is what a POC needs.
   * Raster tiles mean we forgo GL's vector advantages (data-driven styling of
   * the basemap, smooth rotation, tile-level filtering) — our own operational
   * layers are still true GL vector layers, so clustering, heatmaps and
   * zoom-dependent rendering all work regardless. */
  osmRaster: {
    id: "osmRaster",
    kind: PROVIDER_KIND.RASTER,
    label: "OpenStreetMap (raster, demo)",
    tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
    tileSize: 256,
    maxzoom: 19,
    attribution: OSM_ATTRIBUTION,
    productionSafe: false,
    note: "Volunteer-run. Acceptable for POC only — see PRODUCTION_NOTE.",
  },

  /* Self-hosted raster or vector tiles on department infrastructure. This is
   * the shape a real deployment takes: an internal tile server, no third
   * party, works on an air-gapped network. Configure via env, not code. */
  selfHosted: {
    id: "selfHosted",
    kind: process.env.REACT_APP_TILE_KIND === "vector"
      ? PROVIDER_KIND.VECTOR
      : PROVIDER_KIND.RASTER,
    label: "Self-hosted tile server",
    tiles: process.env.REACT_APP_TILE_URL
      ? [process.env.REACT_APP_TILE_URL]
      : null,
    styleUrl: process.env.REACT_APP_TILE_STYLE_URL || null,
    tileSize: 256,
    maxzoom: 19,
    attribution: OSM_ATTRIBUTION,
    productionSafe: true,
    note: "Set REACT_APP_TILE_URL (raster) or REACT_APP_TILE_STYLE_URL (vector).",
  },
};

/* ──────────────────────────────────────────────────────────────────────────
 * BASEMAP REGISTRY — including options we deliberately do NOT enable.
 *
 * Findings from checking the actual licences (2026-09), recorded here so the
 * next person does not have to repeat the research or, worse, assume:
 *
 *  · OpenStreetMap raster — ODbL. Free to use with attribution. Volunteer
 *    infrastructure, so fine for a POC, NOT for production load (§12).
 *
 *  · Sentinel-2 cloudless (EOX, s2maps) — the obvious "free satellite"
 *    answer, and it does not survive contact with the licence. The 2016
 *    mosaic was CC BY 4.0, but EOX no longer advertises it; the live service
 *    offers 2017-2025 only, all CC BY-NC-SA. Non-commercial is legally
 *    ambiguous for a state government portal, and ShareAlike propagates to
 *    derivatives. Not enabled.
 *
 *  · Bhuvan (ISRO/NRSC) — the RIGHT answer for an Indian government portal:
 *    national imagery, OGC WMS/WMTS, department-to-department. But its terms
 *    grant a non-transferable licence "for viewing purposes only" and forbid
 *    redistribution without prior written authorisation from DOS/ISRO/NRSC.
 *    So it cannot simply be embedded — it must be requested. That request is
 *    routine between government bodies and is the recommended path.
 *
 *  · Esri / Google / Mapbox satellite — commercial terms plus an API key,
 *    and a key cannot live in a client bundle (§11, §16). Would require a
 *    server-side proxy holding the credential.
 *
 * THE SAFE PRODUCTION PATH, in order of preference:
 *   1. Bhuvan under written authorisation from NRSC.
 *   2. Self-hosted tiles rendered from Copernicus Sentinel-2 source data.
 *      The underlying Copernicus data is free, full and open — it is EOX's
 *      rendered mosaic that carries the NC clause, not the data itself. Self
 *      rendering also satisfies §12's self-hosting and offline requirements.
 *   3. A commercial provider under contract, proxied server-side.
 * ────────────────────────────────────────────────────────────────────────── */

/* Keyless vector styles. Vector is what MapLibre is actually built for:
 * labels stay crisp at every zoom and rotate with the map, roads are real
 * geometry rather than baked pixels, and the whole style is restyleable.
 *
 * OpenFreeMap serves complete styles with CORS enabled and no API key, and
 * publishes its tiles for self-hosting — which is the same requirement §12
 * places on the raster path. Verified live before wiring: HTTP 200,
 * Access-Control-Allow-Origin: *.
 *
 * A vector basemap is a WHOLE STYLE, not a layer, so switching to it means
 * setStyle() — see the re-installation note in KumbhGeoMap. */
export const VECTOR_STYLES = {
  vectorLiberty: {
    id: "vectorLiberty",
    kind: PROVIDER_KIND.VECTOR,
    label: "Street (vector)",
    styleUrl: "https://tiles.openfreemap.org/styles/liberty",
    attribution: OSM_ATTRIBUTION,
    productionSafe: false,
    note: "Free public endpoint. Self-host before production load.",
  },
  vectorPositron: {
    id: "vectorPositron",
    kind: PROVIDER_KIND.VECTOR,
    label: "Muted (vector)",
    styleUrl: "https://tiles.openfreemap.org/styles/positron",
    attribution: OSM_ATTRIBUTION,
    productionSafe: false,
    note: "Low-contrast base — operational layers dominate. Good for a command wall.",
  },
};

export const BASEMAP_OPTIONS = [
  {
    id: "osmRaster",
    label: { en: "Street (raster)", mr: "रस्ता (रास्टर)" },
    kind: PROVIDER_KIND.RASTER,
    usable: true,
    licence: "ODbL — attribution required",
  },
  {
    id: "vectorLiberty",
    label: { en: "Street (vector)", mr: "रस्ता (व्हेक्टर)" },
    kind: PROVIDER_KIND.VECTOR,
    usable: true,
    licence: "ODbL — OpenStreetMap data, served by OpenFreeMap",
  },
  {
    id: "vectorPositron",
    label: { en: "Muted (vector)", mr: "फिकट (व्हेक्टर)" },
    kind: PROVIDER_KIND.VECTOR,
    usable: true,
    licence: "ODbL — OpenStreetMap data, served by OpenFreeMap",
  },
  {
    id: "satelliteSelfHosted",
    label: { en: "Satellite (self-hosted)", mr: "उपग्रह (स्व-होस्टेड)" },
    usable: Boolean(process.env.REACT_APP_SATELLITE_URL),
    licence: "Depends on the source you render; Copernicus Sentinel-2 is free/full/open",
    blockedReason: "Set REACT_APP_SATELLITE_URL to a tile server you control.",
  },
  {
    id: "bhuvan",
    label: { en: "Satellite (Bhuvan / ISRO)", mr: "उपग्रह (भुवन / इस्रो)" },
    usable: Boolean(process.env.REACT_APP_BHUVAN_URL),
    licence: "DOS/ISRO/NRSC — viewing only; redistribution needs written authorisation",
    blockedReason:
      "Requires written authorisation from NRSC, then set REACT_APP_BHUVAN_URL. " +
      "Recommended production option for a Government of Maharashtra portal.",
  },
  {
    id: "s2cloudless",
    label: { en: "Satellite (raster)", mr: "उपग्रह (रास्टर)" },
    kind: PROVIDER_KIND.RASTER,
    usable: true,
    demoOnly: true,
    licence: "CC BY-NC-SA 4.0 — attribution required, non-commercial",
    blockedReason:
      "Enabled for DEMONSTRATION only. Before any deployment, replace with " +
      "Bhuvan under NRSC authorisation or self-rendered Copernicus tiles: the " +
      "NC clause is ambiguous for government use and ShareAlike propagates.",
  },
];

/* Satellite providers, configured only from env so nothing ships enabled
 * that we lack rights to. */
/* ── TERRAIN / HILLSHADE: attempted, removed, do not re-add as-is ──────
 *
 * Shaded relief would genuinely help here — the Godavari valley and the
 * Brahmagiri ridge shape crowd flow and evacuation routes. The obvious free
 * source is the Terrarium DEM at
 *   https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png
 * It serves 200 OK with real data, which is why it looks like it works.
 *
 * It does NOT work in a browser: the bucket sends no
 * Access-Control-Allow-Origin header, so every tile fetch is blocked by
 * CORS. MapLibre then emits a stream of source errors. Verified directly —
 * HTTP 200, 33KB, no ACAO header.
 *
 * To add relief properly, either proxy the DEM through a server that adds
 * CORS headers, or self-host the tiles alongside the basemap (which §12
 * wants anyway). Adding the public URL back will blank the map. */

export const S2_ATTRIBUTION =
  'Sentinel-2 cloudless by <a href="https://eox.at" target="_blank" rel="noopener noreferrer">EOX IT Services GmbH</a> ' +
  "(Contains modified Copernicus Sentinel data)";

export const SATELLITE_PROVIDERS = {
  /* NOTE THE AXIS ORDER: this is a WMTS endpoint, so the path is
   * {z}/{y}/{x} — row before column. MapLibre's raster template defaults to
   * {z}/{x}/{y}, and using that order here returns a 116-byte blank tile for
   * every request, which looks exactly like "satellite imagery is broken"
   * rather than "the URL is wrong". */
  s2cloudless: {
    id: "s2cloudless",
    kind: PROVIDER_KIND.RASTER,
    label: "Satellite (Sentinel-2, demo)",
    tiles: ["https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2024_3857/default/GoogleMapsCompatible/{z}/{y}/{x}.jpg"],
    tileSize: 256,
    maxzoom: 15,          // the mosaic has no detail beyond this
    attribution: S2_ATTRIBUTION,
    productionSafe: false,
    demoOnly: true,
  },
  satelliteSelfHosted: {
    id: "satelliteSelfHosted",
    kind: PROVIDER_KIND.RASTER,
    label: "Satellite (self-hosted)",
    tiles: process.env.REACT_APP_SATELLITE_URL ? [process.env.REACT_APP_SATELLITE_URL] : null,
    tileSize: 256,
    maxzoom: 18,
    attribution:
      process.env.REACT_APP_SATELLITE_ATTRIBUTION ||
      "Imagery © the operating department",
    productionSafe: true,
  },
  bhuvan: {
    id: "bhuvan",
    kind: PROVIDER_KIND.RASTER,
    label: "Satellite (Bhuvan / ISRO)",
    tiles: process.env.REACT_APP_BHUVAN_URL ? [process.env.REACT_APP_BHUVAN_URL] : null,
    tileSize: 256,
    maxzoom: 18,
    /* Bhuvan attribution is a licence condition, not a courtesy. */
    attribution: "Imagery © DOS/ISRO/NRSC — Bhuvan, used under authorisation",
    productionSafe: true,
    requiresAuthorisation: true,
  },
};

/* Which provider to use. Env-selectable so a deployment can point at internal
 * infrastructure without a rebuild of application logic. Falls back to the
 * keyless demo provider when nothing is configured, rather than failing —
 * graceful degradation (§15) beats a blank screen. */
export function resolveProvider() {
  const requested = process.env.REACT_APP_TILE_PROVIDER;
  const provider = TILE_PROVIDERS[requested] || TILE_PROVIDERS.osmRaster;

  // A self-hosted provider that was selected but never configured would render
  // an empty map. Fall back and say so, instead of silently showing nothing.
  if (provider.id === "selfHosted" && !provider.tiles && !provider.styleUrl) {
    return {
      ...TILE_PROVIDERS.osmRaster,
      fallbackReason:
        "REACT_APP_TILE_PROVIDER=selfHosted but no tile URL configured; using demo tiles.",
    };
  }
  return provider;
}

/* Build a MapLibre style document from the resolved provider.
 *
 * Returning a style object (rather than a URL) keeps the basemap swappable:
 * raster and vector providers both reduce to "a style", and the map component
 * never branches on provider kind. */
/* Resolve a basemap by id, falling back to the street map when the requested
 * one is not configured. Never silently renders nothing. */
export function resolveBasemap(id) {
  if (SATELLITE_PROVIDERS[id]) {
    const p = SATELLITE_PROVIDERS[id];
    if (p.tiles) return p;
    return { ...resolveProvider(), fallbackReason: `${p.label} is not configured; using the street map.` };
  }
  return resolveProvider();
}

/* Is this basemap a whole style (vector) or a layer inside our raster style? */
export function isVectorBasemap(id) {
  return Boolean(VECTOR_STYLES[id]);
}
export function vectorStyleUrl(id) {
  return VECTOR_STYLES[id]?.styleUrl || null;
}

export function buildBaseStyle(provider = resolveProvider()) {
  if (provider.kind === PROVIDER_KIND.VECTOR && provider.styleUrl) {
    return provider.styleUrl; // MapLibre accepts a style URL directly.
  }

  return {
    version: 8,
    /* Glyphs are needed for any text layer. Vector providers ship their own;
     * for raster we point at a public glyph source. A self-hosted deployment
     * should serve glyphs internally too — see PRODUCTION_NOTE. */
    glyphs: process.env.REACT_APP_GLYPHS_URL ||
      "https://fonts.openmaptiles.org/{fontstack}/{range}.pbf",
    sources: {
      satellite: {
        type: "raster",
        tiles: SATELLITE_PROVIDERS.s2cloudless.tiles,
        tileSize: SATELLITE_PROVIDERS.s2cloudless.tileSize,
        maxzoom: SATELLITE_PROVIDERS.s2cloudless.maxzoom,
        attribution: S2_ATTRIBUTION,
      },
      basemap: {
        type: "raster",
        tiles: provider.tiles,
        tileSize: provider.tileSize,
        maxzoom: provider.maxzoom,
        attribution: provider.attribution,
      },
    },
    layers: [
      /* A neutral ground colour underneath, so tile load gaps read as the map
       * still loading rather than as holes in the data. */
      { id: "background", type: "background", paint: { "background-color": "#EAE6DF" } },
      /* Satellite sits beneath the street layer and starts hidden. Keeping
       * both in one style means switching is a visibility toggle rather than
       * a setStyle() call — the latter would tear down every operational
       * source and layer and rebuild them on each switch. */
      {
        id: "satellite",
        type: "raster",
        source: "satellite",
        layout: { visibility: "none" },
        paint: { "raster-brightness-max": 0.95, "raster-contrast": 0.05 },
      },
      {
        id: "basemap",
        type: "raster",
        source: "basemap",
        paint: {
          /* Desaturated and slightly dimmed. A command map's basemap is
           * context, not content — operational layers must read on top of it.
           * This is the single biggest reason it does not look like a
           * consumer navigation map. */
          /* Desaturated so operational layers dominate, but no longer
             dimmed or made translucent. An earlier version dropped opacity
             to 0.88 so a hillshade underneath could show through — which
             drained the street map instead. Relief now sits ABOVE this
             layer, so the basemap can stay at full strength. */
          "raster-saturation": -0.55,
          "raster-brightness-min": 0.0,
          "raster-brightness-max": 1.0,
          "raster-contrast": 0.12,
        },
      },
    ],
  };
}

/* ──────────────────────────────────────────────────────────────────────────
 * PRODUCTION_NOTE — read before any deployment beyond a laptop demo.
 *
 * 1. osm.org tiles are not production infrastructure. Their usage policy
 *    forbids heavy/commercial use and they will rate-limit or block. Before
 *    go-live, move to one of:
 *      - self-hosted raster (e.g. renderd/mod_tile) on department infra
 *      - self-hosted vector (planetiler/tileserver-gl) — preferred, unlocks
 *        MapLibre's vector capabilities and works offline
 *      - a commercial provider under contract
 * 2. Glyph and sprite endpoints must move in-house alongside tiles, or the
 *    map still calls out to the public internet for labels.
 * 3. Caching: put a reverse proxy in front of the tile source so an
 *    operations centre losing upstream connectivity degrades to cached tiles
 *    rather than a blank map.
 * 4. No provider key belongs in this file or any client bundle. A keyed
 *    provider must be proxied server-side (§11/§16) — the client calls our
 *    proxy, the proxy holds the key.
 * ────────────────────────────────────────────────────────────────────────── */
